import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useId } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';

import { useCarValuationToolContext } from '../../../../CarValuationTool.context';

import { Button } from 'components/Toolkit/Button';
import { Text } from 'components/ToolkitV2/Text/Text';
import { RegistrationNumberField } from 'components/Toolkit/Inputs/RegistrationNumberField';
import { TextField } from 'components/Toolkit/Inputs/TextField';
import { Select } from 'components/Toolkit/Inputs/Select';
import { Label } from 'components/Toolkit/Inputs/Label';
import { SubText } from 'components/Toolkit/Inputs/SubText';
import { ErrorMessage } from 'components/Toolkit/Inputs/ErrorMessage';

import {
  PrivateValuationFormInputs,
  mileageUnitSchema,
  privateValuationSubmissionSchema,
} from '../../../../queries/schema/PrivateValuationSchema';

import * as Styled from './ValueCarForm.styled';

import { Spacer } from 'helpers/genericStyles';
import { formatNumber } from 'helpers/formatting';

const ValueCarForm = ({
  onSubmitCallback,
}: {
  onSubmitCallback: (args: PrivateValuationFormInputs) => void;
}) => {
  const { registrationNumber: initialRegistrationNumber } =
    useCarValuationToolContext();

  const { control, handleSubmit, getFieldState, formState } =
    useForm<PrivateValuationFormInputs>({
      resolver: zodResolver(privateValuationSubmissionSchema),
      defaultValues: {
        registrationNumber: initialRegistrationNumber,
        mileageUnit: mileageUnitSchema.enum.Kilometres,
        mileage: '',
      },
      mode: 'onSubmit',
    });

  const onSubmit: SubmitHandler<PrivateValuationFormInputs> = (values) => {
    onSubmitCallback(values);
  };

  const formId = useId();
  /**
   * We use a single label to describe mileage, a text input for the value and a select input for the unit.
   * These IDs are used to connect those inputs with the label.
   */
  const mileageId = `${formId}-mileage`;
  const odometerId = `${formId}-odometer-reading`;

  const mileageFieldState = getFieldState('mileage', formState);
  const mileageUnitFieldState = getFieldState('mileageUnit', formState);
  const mileageErrorMessage =
    mileageFieldState.error?.message ??
    mileageUnitFieldState.error?.message ??
    '';

  const MAX_LENGTH_OF_NINE_DIGITS_WITH_THOUSANDS_SEPARATED_BY_COMMAS = 11;

  return (
    <Styled.Form onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Text size="M16">
          Enter your registration number and mileage and we'll value your car
          instantly.
        </Text>
        <Spacer marginBottom="M16" />
        <Controller
          control={control}
          name="registrationNumber"
          render={({ field, fieldState: { error } }) => {
            return (
              <RegistrationNumberField
                errorMessage={error?.message}
                id={`${formId}-${field.name}`}
                label="Registration Number"
                {...field}
              />
            );
          }}
        />
        <Label htmlFor={mileageId} id={odometerId}>
          Odometer Reading
        </Label>
        <Styled.MileageContainer>
          <Controller
            control={control}
            name="mileage"
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  {...field}
                  placeholder="30,000"
                  id={mileageId}
                  hasError={Boolean(error?.message)}
                  willUseSubText={false}
                  value={
                    field.value &&
                    formatNumber(parseInt(field.value))?.toString()
                  }
                  onChange={(value: string) => {
                    field.onChange(value.replace(/\D/gi, ''));
                  }}
                  maxLength={
                    MAX_LENGTH_OF_NINE_DIGITS_WITH_THOUSANDS_SEPARATED_BY_COMMAS
                  }
                  inputMode="numeric"
                />
              );
            }}
          />
          <Controller
            control={control}
            name="mileageUnit"
            render={({ field, fieldState: { error } }) => {
              const mileageUnitId = `${formId}-${field.name}`;

              const mileageUnitOptions = [
                {
                  value: mileageUnitSchema.enum.Kilometres,
                  label: 'km',
                },
                {
                  value: mileageUnitSchema.enum.Miles,
                  label: 'miles',
                },
              ];

              return (
                <Select
                  {...field}
                  options={mileageUnitOptions}
                  id={mileageUnitId}
                  hasError={Boolean(error?.message)}
                  willUseSubText={false}
                  aria-labelledby={odometerId}
                />
              );
            }}
          />
        </Styled.MileageContainer>
        <SubText>
          {mileageErrorMessage && <ErrorMessage text={mileageErrorMessage} />}
        </SubText>
      </div>
      <Button type="submit" data-tracking="cvonadp-value-my-car">
        Value my car
      </Button>
    </Styled.Form>
  );
};

export { ValueCarForm };
